import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { ArrowDown, ArrowUp, ChevronRight } from "lucide-react";
import { brand, neutral } from "../../theme/palette";
import { formatINR } from "../../utils/formatINR";
import { formatCompactINR, formatDecimal, formatDuration, formatNumber, stationPerformance } from "../../utils/analyticsMetrics";
import { ChartCard, SegmentedControl, TableSkeleton, axisProps } from "./AnalyticsUI";

const METRICS = {
  revenue: { label: "Revenue", format: formatINR, axis: formatCompactINR },
  energy: { label: "Energy", format: (v) => `${formatDecimal(v)} kWh`, axis: (v) => formatNumber(v) },
  sessions: { label: "Sessions", format: formatNumber, axis: formatNumber },
};

const COLUMNS = [
  { key: "name", label: "Station", align: "left" },
  { key: "sessions", label: "Sessions", align: "right" },
  { key: "energy", label: "Energy (kWh)", align: "right" },
  { key: "revenue", label: "Revenue", align: "right" },
  { key: "avgDurationSeconds", label: "Avg duration", align: "right" },
  { key: "share", label: "Share of revenue", align: "left" },
];

const displayName = (s) => s.name || s.stationId;

const StationPerformance = ({ sessions, stationNames, isLoading, error }) => {
  const navigate = useNavigate();
  const [metric, setMetric] = useState("revenue");
  const [sort, setSort] = useState({ key: "revenue", dir: "desc" });
  const [showAll, setShowAll] = useState(false);

  const stations = useMemo(() => stationPerformance(sessions, stationNames), [sessions, stationNames]);
  const totalRevenue = useMemo(() => stations.reduce((a, s) => a + s.revenue, 0), [stations]);

  const ranking = useMemo(
    () => [...stations].sort((a, b) => b[metric] - a[metric]).slice(0, 10).map((s) => ({ ...s, label: displayName(s) })),
    [stations, metric]
  );
  const lowest = useMemo(
    () => (stations.length >= 6 ? [...stations].sort((a, b) => a.revenue - b.revenue).slice(0, 5) : []),
    [stations]
  );

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const value = (s) => (sort.key === "name" ? displayName(s).toLowerCase() : sort.key === "share" ? s.revenue : s[sort.key]);
    return [...stations].sort((a, b) => (value(a) > value(b) ? dir : value(a) < value(b) ? -dir : 0));
  }, [stations, sort]);
  const visible = showAll ? sorted : sorted.slice(0, 10);

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "name" ? "asc" : "desc" }));

  const empty = !stations.length;
  const fmt = METRICS[metric];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard
          className="lg:col-span-2"
          title="Top stations"
          description={`Ranked by ${fmt.label.toLowerCase()} in the selected period.`}
          actions={
            <SegmentedControl
              options={Object.entries(METRICS).map(([value, m]) => ({ value, label: m.label }))}
              value={metric}
              onChange={setMetric}
            />
          }
          isLoading={isLoading}
          error={error}
          isEmpty={empty}
          height={Math.max(200, ranking.length * 34)}
        >
          <ResponsiveContainer width="100%" height={Math.max(200, ranking.length * 34)}>
            <BarChart data={ranking} layout="vertical" margin={{ top: 0, right: 56, bottom: 0, left: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="label" {...axisProps} width={140} tick={{ ...axisProps.tick, fontSize: 12, fill: neutral[700] }} />
              <Tooltip
                cursor={{ fill: neutral[100] }}
                content={({ active, payload }) =>
                  active && payload?.length ? (
                    <div className="rounded-lg border border-line bg-white px-3 py-2.5 text-13 shadow-lg">
                      <p className="mb-1 font-medium text-fg">{payload[0].payload.label}</p>
                      <p className="text-xs text-muted">{payload[0].payload.stationId}</p>
                      <p className="mt-1 tabular-nums font-semibold">{fmt.format(payload[0].value)}</p>
                    </div>
                  ) : null
                }
              />
              <Bar dataKey={metric} radius={[0, 3, 3, 0]} maxBarSize={20} label={{ position: "right", fontSize: 11, fill: neutral[600], formatter: fmt.axis }}>
                {ranking.map((s, i) => <Cell key={s.stationId} fill={i === 0 ? brand[600] : brand[300]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Needs attention"
          description="Lowest revenue stations with at least one session."
          isLoading={isLoading}
          error={error}
          isEmpty={!lowest.length}
          emptyText={empty ? "No data for the selected period" : "Not enough stations to compare"}
          height={200}
        >
          <ul className="divide-y divide-line">
            {lowest.map((s) => (
              <li key={s.stationId}>
                <button
                  type="button"
                  onClick={() => navigate(`/charging-stations/${s.stationId}`)}
                  className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:bg-neutral-50"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-13 font-medium text-fg">{displayName(s)}</span>
                    <span className="block text-xs text-muted">{formatNumber(s.sessions)} sessions · {formatDecimal(s.energy)} kWh</span>
                  </span>
                  <span className="text-13 font-semibold tabular-nums text-fg">{formatINR(s.revenue)}</span>
                </button>
              </li>
            ))}
          </ul>
        </ChartCard>
      </div>

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between border-b border-line px-5 py-4">
          <div>
            <h3 className="section-title">All stations</h3>
            <p className="mt-0.5 text-13 text-muted">Click a column to sort, or a station to open its details.</p>
          </div>
          {!isLoading && !error && <span className="text-13 text-muted tabular-nums">{formatNumber(stations.length)} stations</span>}
        </div>
        {isLoading ? (
          <div className="px-5 py-3"><TableSkeleton rows={6} /></div>
        ) : error ? (
          <p className="px-5 py-10 text-center text-13 text-red-700">Couldn't load station data. {error}</p>
        ) : empty ? (
          <p className="px-5 py-10 text-center text-13 text-muted">No charging activity in the selected period.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-13">
                <thead className="border-b border-line">
                  <tr>
                    {COLUMNS.map((c) => (
                      <th key={c.key} className={`h-10 px-4 uppercase ${c.align === "right" ? "text-right" : "text-left"}`}>
                        <button type="button" onClick={() => toggleSort(c.key)} className={`inline-flex items-center gap-1 uppercase hover:text-fg ${sort.key === c.key ? "text-fg" : ""}`}>
                          {c.label}
                          {sort.key === c.key && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
                        </button>
                      </th>
                    ))}
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/70">
                  {visible.map((s) => {
                    const share = totalRevenue ? s.revenue / totalRevenue : 0;
                    return (
                      <tr
                        key={s.stationId}
                        onClick={() => navigate(`/charging-stations/${s.stationId}`)}
                        className="cursor-pointer odd:bg-surface even:bg-neutral-100 hover:!bg-brand-50/60"
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium text-fg">{displayName(s)}</div>
                          {s.name && <div className="text-xs text-muted">{s.stationId}{s.location ? ` · ${s.location}` : ""}</div>}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-neutral-700">{formatNumber(s.sessions)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-neutral-700">{formatDecimal(s.energy)}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums text-fg">{formatINR(s.revenue)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-neutral-700">{formatDuration(s.avgDurationSeconds)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-neutral-200">
                              <div className="h-full rounded-full bg-brand-600" style={{ width: `${share * 100}%` }} />
                            </div>
                            <span className="w-12 text-xs tabular-nums text-muted">{(share * 100).toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="pr-3 text-neutral-300"><ChevronRight className="h-4 w-4" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {sorted.length > 10 && (
              <div className="border-t border-line px-5 py-3 text-center">
                <button type="button" onClick={() => setShowAll((v) => !v)} className="btn-ghost">
                  {showAll ? "Show top 10" : `Show all ${formatNumber(sorted.length)} stations`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StationPerformance;
